import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { IconComponent } from '../icon';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CommonModule, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ],
  template: `
    <div class="space-y-3">
      <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
        <input
          type="file"
          [accept]="accept"
          [multiple]="multiple"
          (change)="onFileSelected($event)"
          class="hidden"
          #fileInput
        />
        <div class="flex flex-col items-center justify-center">
          <app-icon name="upload-cloud" class="w-12 h-12 text-gray-400 mb-3" />
          <button
            type="button"
            (click)="fileInput.click()"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mb-2"
          >
            Selecionar {{ multiple ? 'Arquivos' : 'Arquivo' }}
          </button>
          <p class="text-sm text-gray-500">{{ hint }}</p>
          <p class="text-xs text-gray-400 mt-1" *ngIf="maxSize">Tamanho máximo: {{ maxSize }}MB</p>
        </div>
      </div>

      <div *ngIf="files.length > 0" class="space-y-2">
        <div
          *ngFor="let file of files; let i = index"
          class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
        >
          <div class="flex items-center space-x-3 flex-1">
            <app-icon name="file" class="w-5 h-5 text-gray-500" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</p>
              <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
            </div>
          </div>
          <button
            type="button"
            (click)="removeFile(i)"
            class="ml-3 text-red-600 hover:text-red-800"
          >
            <app-icon name="x" class="w-5 h-5" />
          </button>
        </div>
      </div>

      <p *ngIf="error" class="text-sm text-red-600">{{ error }}</p>
    </div>
  `
})
export class FileUploadComponent implements ControlValueAccessor {
  @Input() accept = '*/*';
  @Input() multiple = false;
  @Input() maxSize?: number; // in MB
  @Input() hint = 'Clique para selecionar ou arraste arquivos aqui';
  @Output() filesChange = new EventEmitter<File[]>();

  files: File[] = [];
  error = '';

  private onChange: (files: File[]) => void = () => {};
  private onTouched: () => void = () => {};

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFiles = Array.from(input.files);
      this.error = '';

      // Validate file size
      if (this.maxSize) {
        const invalidFiles = selectedFiles.filter(
          file => file.size > this.maxSize! * 1024 * 1024
        );
        if (invalidFiles.length > 0) {
          this.error = `Alguns arquivos excedem o tamanho máximo de ${this.maxSize}MB`;
          return;
        }
      }

      if (this.multiple) {
        this.files = [...this.files, ...selectedFiles];
      } else {
        this.files = selectedFiles;
      }

      this.filesChange.emit(this.files);
      this.onChange(this.files);
      this.onTouched();
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.filesChange.emit(this.files);
    this.onChange(this.files);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  // ControlValueAccessor implementation
  writeValue(value: File[]): void {
    this.files = value || [];
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
